import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import toast, { Toaster } from 'react-hot-toast';
import { apiCall } from "../utils/api";

const operators = [
  {
    input_sl_number: "1428",
    operator_id: "108",
    name: "Tata Play",
    logo: "TP",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op19.png",
    color: "#1A237E",
    bgColor: "#1A237E10",
    minLength: 10,
    maxLength: 10
  },
  {
    input_sl_number: "1425",
    operator_id: "105",
    name: "Airtel DTH",
    logo: "AD",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op23.png",
    color: "#E40000",
    bgColor: "#E4000010",
    minLength: 10,
    maxLength: 10
  },
  {
    input_sl_number: "1426",
    operator_id: "106",
    name: "Dish TV",
    logo: "DT",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op18.png",
    color: "#F57C00",
    bgColor: "#F57C0010",
    minLength: 11,
    maxLength: 11
  },
  {
    input_sl_number: "1427",
    operator_id: "107",
    name: "Sun Direct",
    logo: "SD",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op22.png",
    color: "#FF5722",
    bgColor: "#FF572210",
    minLength: 11,
    maxLength: 11
  },
  {
    input_sl_number: "1429",
    operator_id: "109",
    name: "Videocon D2H",
    logo: "VD",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op21.png",
    color: "#7B1FA2",
    bgColor: "#7B1FA210",
    minLength: 8,
    maxLength: 10
  },
];

function DthRecharge() {
  const navigate = useNavigate();
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [subscriberId, setSubscriberId] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");

  const handleOperatorSelect = (op) => {
    setSelectedOperator(op);
    setSubscriberId("");
    setError("");
  };

  const validateSubscriberId = (id, operator) => {
    const len = id.length;
    if (len < operator.minLength) {
      return `Enter ${operator.minLength}-digit subscriber ID (min ${operator.minLength} digits)`;
    }
    if (len > operator.maxLength) {
      return `Enter ${operator.maxLength}-digit subscriber ID (max ${operator.maxLength} digits)`;
    }
    return null;
  };

  const handleSubscriberIdChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    // Limit input based on operator's maxLength
    if (selectedOperator && value.length > selectedOperator.maxLength) {
      return;
    }

    setSubscriberId(value);
    setError("");

    // Validate in real-time if length matches requirements
    if (selectedOperator && value.length === selectedOperator.minLength) {
      const validationError = validateSubscriberId(value, selectedOperator);
      if (validationError) {
        setError(validationError);
      }
    }
  };

  const handleFetchDetails = async () => {
    if (!selectedOperator) {
      setError("Please select an operator first");
      return;
    }

    const validationError = validateSubscriberId(subscriberId, selectedOperator);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setIsFetching(true);

    try {
      const result = await apiCall(
        "bill-fetch",
        {
          operator_id: selectedOperator.operator_id,
          input_sl_number: selectedOperator.input_sl_number,
          form: {
            param1: subscriberId
          }
        },
        true
      );

      if (result.status === true) {
        const billInfo = result.bill_info || {};

        navigate("/payment-confirmation", {
          state: {
            service: "DTH Recharge",
            operator: selectedOperator.name,
            operator_id: selectedOperator.operator_id,
            number: billInfo.cellNumber || "",
            amount: billInfo.billnetamount,
            consumerName: billInfo.userName || "",
            serviceSpecificData: {
              range: result.range,
              dueDate: billInfo.dueDate || "A",
              balance: billInfo.balance || "0",
              form: {
                param1: subscriberId
              }
            }
          }
        });
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  // Get placeholder text based on selected operator
  const getPlaceholderText = () => {
    if (!selectedOperator) return "Enter subscriber ID";
    if (selectedOperator.minLength === selectedOperator.maxLength) {
      return `Enter ${selectedOperator.minLength}-digit subscriber ID`;
    }
    return `Enter ${selectedOperator.minLength}-${selectedOperator.maxLength} digit subscriber ID`;
  };

  return (
    <>
      <Header title={"DTH Recharge"} showBack={true} />
      <div className="p-4">
        <h3
          className="text-sm font-bold mb-5"
          style={{ color: APP_THEME.textPrimary }}
        >
          Select DTH Operator
        </h3>

        {/* Operator Grid - 4 Columns */}
        <div className={`grid grid-cols-4 gap-3 transition-all duration-300 ${selectedOperator ? 'mb-7' : 'mb-0'}`}>
          {operators.map((op) => {
            const isSelected = selectedOperator?.input_sl_number === op.input_sl_number;
            return (
              <div
                key={op.input_sl_number}
                onClick={() => handleOperatorSelect(op)}
                className="flex flex-col items-center gap-2 cursor-pointer transition-all duration-200 active:scale-95"
              >
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center transition-all duration-200 p-3"
                  style={{
                    backgroundColor: isSelected ? op.bgColor : APP_THEME.surfaceColor,
                    border: `2px solid ${isSelected ? op.color : APP_THEME.borderColor}`,
                    boxShadow: isSelected ? `0 4px 12px ${op.color}33` : "none",
                  }}
                >
                  <img
                    src={op.iconUrl}
                    alt={op.name}
                    className="w-9 h-9 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<div class="w-9 h-9 rounded-full flex items-center justify-center text-xs font-extrabold text-white" style="background-color: ${op.color}">${op.logo}</div>`;
                    }}
                  />
                </div>
                <span
                  className="text-[10px] font-bold text-center"
                  style={{ color: isSelected ? op.color : APP_THEME.textSecondary }}
                >
                  {op.name}
                </span>
              </div>
            );
          })}
        </div>

        {/* Inline Input (Expands when operator is selected) */}
        {selectedOperator && (
          <div
            className="animate-fadeIn rounded-2xl p-5 border shadow-sm"
            style={{
              backgroundColor: APP_THEME.surfaceColor,
              borderColor: APP_THEME.borderColor,
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)"
            }}
          >
            <div className="mb-5">
              <label
                className="block text-xs font-bold mb-2 pl-1"
                style={{ color: APP_THEME.textPrimary }}
              >
                Subscriber ID for {selectedOperator.name}
                <span className="text-[10px] font-normal ml-1" style={{ color: APP_THEME.textSecondary }}>
                  ({selectedOperator.minLength === selectedOperator.maxLength
                    ? `${selectedOperator.minLength} digits`
                    : `${selectedOperator.minLength}-${selectedOperator.maxLength} digits`})
                </span>
              </label>
              <div
                className="flex items-center rounded-xl overflow-hidden transition-colors duration-200"
                style={{
                  border: `1px solid ${error ? APP_THEME.errorColor : APP_THEME.borderColor}`,
                  backgroundColor: APP_THEME.bgColor,
                }}
              >
                <input
                  type="tel"
                  placeholder={getPlaceholderText()}
                  value={subscriberId}
                  onChange={handleSubscriberIdChange}
                  maxLength={selectedOperator.maxLength}
                  className="flex-1 px-4 py-3 border-none text-base font-semibold outline-none bg-transparent"
                  style={{ color: APP_THEME.textPrimary }}
                  autoFocus
                />
              </div>
              {error && (
                <div
                  className="text-xs mt-1.5 pl-1 font-semibold"
                  style={{ color: APP_THEME.errorColor }}
                >
                  {error}
                </div>
              )}
              {!error && subscriberId && (
                <div
                  className="text-[10px] mt-1.5 pl-1"
                  style={{ color: APP_THEME.textSecondary }}
                >
                  {subscriberId.length}/{selectedOperator.maxLength} digits
                </div>
              )}
            </div>

            <button
              onClick={handleFetchDetails}
              disabled={isFetching || !subscriberId || subscriberId.length !== selectedOperator.maxLength}
              className="w-full py-3 border-none rounded-lg text-sm font-bold cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 active:scale-97 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: APP_THEME.primaryColor,
                color: APP_THEME.textOnPrimary,
                borderRadius: APP_THEME.borderRadiusBtn,
                boxShadow: `0 4px 12px ${APP_THEME.primaryColor}33`,
              }}
            >
              {isFetching ? (
                <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `3px solid #ffffff33`, borderTopColor: "#fff" }} />
              ) : "View Customer Details"}
            </button>
          </div>
        )}
      </div>

      <Toaster position="bottom-center" />

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.4s ease forwards;
          }
          .animate-slideUp {
            animation: slideUp 0.4s ease forwards;
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          .active\\:scale-97:active {
            transform: scale(0.97);
          }
          .active\\:scale-98:active {
            transform: scale(0.98);
          }
          .active\\:scale-95:active {
            transform: scale(0.95);
          }
        `}
      </style>
    </>
  );
}

export default DthRecharge;